// utils/redsysCheckout.js
//
// Starts a Redsys / Getnet TPV payment (Redirección method).
//
// Flow: ask the backend to create a checkout (it derives the price server-side
// and returns the signed form fields), remember what we're buying so the return
// page can verify access, then auto-submit a hidden form that redirects the
// browser to the Redsys secure payment page.
//
// Returns (when it does NOT redirect):
//   { needLogin: true }        -> caller should prompt login
//   { error: '<message>' }     -> caller should show the message
// On success it redirects the browser and never returns.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PENDING_KEY = 'pending-purchase';

function submitRedsysForm(url, fields) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

/**
 * @param {string} productId
 * @returns {Promise<{needLogin?: boolean, error?: string}>}
 */
export async function startRedsysCheckout(productId) {
  const authToken = localStorage.getItem('auth-token');
  if (!authToken) return { needLogin: true };

  try {
    const response = await fetch(`${BACKEND_URL}/payment/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': authToken,
      },
      body: JSON.stringify({ productId }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      return { error: data.message || 'Could not start the payment. Please try again.' };
    }

    // Remember the product so /payment-success can confirm access once the webhook lands.
    try {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ productId, orderNumber: data.orderNumber })
      );
    } catch (_) {
      /* localStorage unavailable — non-fatal */
    }

    submitRedsysForm(data.url, data.fields);
    return {}; // redirecting
  } catch (error) {
    console.error('Error starting Redsys checkout:', error);
    return { error: 'An error occurred. Please try again.' };
  }
}

export function getPendingPurchase() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
  } catch (_) {
    return null;
  }
}

export function clearPendingPurchase() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (_) {
    /* ignore */
  }
}
